import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { EliminarClaseDeSueloDialog } from "@/components/suelos/EliminarClaseDeSueloDialog";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useClasesDeSuelo,
  useCrearClaseDeSuelo,
  useGuardarClaseDeSuelo,
} from "@/hooks/useClasesDeSuelo";
import { getApiErrorMessage } from "@/lib/api";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import type { ClaseDeSuelo } from "@/lib/suelos";

const FILAS_SKELETON = 6;

/**
 * El catálogo de clases de uso de suelo.
 *
 * QUÉ SE EDITA Y QUÉ NO. La glosa y la posición. El CÓDIGO no: es la identidad
 * con la que el seeder reconoce la fila —renombrarlo haría que la próxima
 * siembra creara un duplicado— y además es lo que el informe IMPRIME, así que
 * una clase renombrada dejaría al selector ofreciendo dos opciones para la
 * misma cosa. Un código mal tipeado se da de baja y se crea el correcto.
 *
 * SÍ SE AGREGAN Y SE DAN DE BAJA, al revés que la escala de homologación.
 * Aquellos cinco ajustes son el método de tasación y no crecen; esto sí,
 * porque la notación admite rangos y sufijos —`IIIr-IVr`, `IV-V`— cuyas
 * combinaciones no están medidas. Esta pantalla es justamente lo que evita que
 * la que falta obligue a un despliegue.
 *
 * LA EDICIÓN ES EN LA PROPIA FILA, con su botón. Un botón único para todas
 * guardaría clases que nadie tocó, y un error en una dejaría sin saber cuáles
 * se escribieron — misma regla que la tarjeta por ajuste de homologación.
 */
export function MantenedorDeClasesDeSuelo() {
  const consulta = useClasesDeSuelo();
  const crear = useCrearClaseDeSuelo();
  const estado = estadoDeConsulta(consulta);

  const clases = consulta.data ?? [];

  const [codigo, setCodigo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [errorDelAlta, setErrorDelAlta] = useState<string | null>(null);
  const [aDarDeBaja, setADarDeBaja] = useState<ClaseDeSuelo | null>(null);

  function agregar() {
    const limpio = codigo.trim();

    if (limpio === "") {
      setErrorDelAlta("Escribe el código de la clase, tal como se imprime.");
      return;
    }

    setErrorDelAlta(null);

    crear.mutate(
      {
        codigo: limpio,
        descripcion: descripcion.trim() === "" ? null : descripcion.trim(),
        // Al FINAL del catálogo. Una clase nueva no puede adivinar su lugar en
        // la clasificación —`IIIr` va después de `III`, pero `IV-V` no va bajo
        // ninguna de las dos—, así que se agrega al final y se reposiciona
        // desde la columna "Orden", que está a la vista.
        orden: clases.length,
      },
      {
        onSuccess: (clase) => {
          toast.success(`Clase «${clase.codigo}» agregada al catálogo`);
          setCodigo("");
          setDescripcion("");
        },
        onError: (error) => {
          setErrorDelAlta(
            getApiErrorMessage(error, "No pudimos agregar la clase. Inténtalo de nuevo."),
          );
        },
      },
    );
  }

  return (
    <div className="space-y-6 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="max-w-[70ch]">
        <p className="text-ink-muted text-caption">
          Con qué se describen los suelos de un predio en la pestaña «Descripción de
          suelos» de un informe.
        </p>
        <p className="text-ink-hint mt-1 text-xs">
          El código es lo que se <strong>imprime</strong> en el cuadro del informe, y no
          se limita a las ocho clases de la clasificación: los informes reales de
          Consultek traen códigos compuestos con rango —<code>IIIr-IVr</code>,{" "}
          <code>IV-V</code>— donde el sufijo <code>r</code> distingue suelo regado. Por eso
          el código no se puede editar: si quedó mal escrito, da de baja la clase y crea
          la correcta.
        </p>
      </div>

      {/* Alta en la misma pantalla que el listado: son dos campos y el
          resultado se ve abajo al instante. */}
      <div className="bg-card w-full max-w-[46rem] rounded-xl border p-6">
        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            agregar();
          }}
          className="flex flex-wrap items-start gap-3"
        >
          <div className="flex w-40 flex-col gap-1.5">
            <Label htmlFor="codigo-nuevo">Código</Label>
            <Input
              id="codigo-nuevo"
              placeholder="Ej: IIIr-IVr"
              autoComplete="off"
              maxLength={20}
              value={codigo}
              onChange={(evento) => setCodigo(evento.target.value)}
            />
          </div>

          <div className="flex min-w-[16rem] flex-1 flex-col gap-1.5">
            <Label htmlFor="descripcion-nueva">Qué es (opcional)</Label>
            <Input
              id="descripcion-nueva"
              placeholder="Ej: Suelo regado, entre clases III y IV."
              autoComplete="off"
              value={descripcion}
              onChange={(evento) => setDescripcion(evento.target.value)}
            />
          </div>

          <Button type="submit" disabled={crear.isPending} className="mt-[1.625rem] gap-2">
            {crear.isPending && <Spinner className="text-primary-foreground" />}
            <Plus className="h-4 w-4" />
            Agregar clase
          </Button>
        </form>

        {errorDelAlta && (
          <p role="alert" className="text-destructive mt-3 text-sm">
            {errorDelAlta}
          </p>
        )}
      </div>

      {estado.sinConexion ? (
        <EstadoSinConexion que="el catálogo de clases de uso de suelo" />
      ) : estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar el catálogo"
          error={consulta.error}
          onReintentar={() => consulta.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.cargando ? (
        <div className="w-full max-w-[46rem] space-y-3">
          {Array.from({ length: FILAS_SKELETON }).map((_, i) => (
            <Skeleton
              key={i}
              role="status"
              aria-label="Cargando clases de uso de suelo"
              className="h-11 w-full rounded-md"
            />
          ))}
        </div>
      ) : clases.length === 0 ? (
        <Empty className="w-full max-w-[46rem] rounded-xl border">
          <EmptyHeader>
            <EmptyTitle>El catálogo está vacío</EmptyTitle>
            <EmptyDescription>
              Agrega la primera clase con el formulario de arriba: sin catálogo, el cuadro
              de suelos de un informe no tiene qué ofrecer.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="w-full max-w-[46rem] space-y-3">
          <p className="text-sm font-bold">
            {clases.length === 1 ? "1 clase" : `${clases.length} clases`}
          </p>

          <div className="bg-card overflow-hidden rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Código</TableHead>
                  <TableHead>Qué es</TableHead>
                  <TableHead className="w-20">Orden</TableHead>
                  <TableHead className="w-44 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clases.map((clase) => (
                  <FilaDeClase
                    key={clase.id}
                    clase={clase}
                    onDarDeBaja={() => setADarDeBaja(clase)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* El `key` REMONTA el diálogo al cambiar de clase: sin él, el rechazo de
          la anterior seguiría en pantalla sobre una que nunca se intentó dar de
          baja. */}
      <EliminarClaseDeSueloDialog
        key={`baja-${aDarDeBaja?.id}`}
        clase={aDarDeBaja}
        onCerrar={() => setADarDeBaja(null)}
      />
    </div>
  );
}

/**
 * Una clase del catálogo, editable en su propia fila.
 *
 * El borrador vive ACÁ y no en la consulta. Con los campos leyendo del caché,
 * cada tecla escribiría en el estado compartido y todas las filas se volverían
 * a dibujar; y al refrescarse la consulta, lo que la persona está escribiendo
 * se perdería sin aviso. Mismo motivo que en `MantenedorDeHomologacion`.
 */
function FilaDeClase({
  clase,
  onDarDeBaja,
}: {
  clase: ClaseDeSuelo;
  onDarDeBaja: () => void;
}) {
  const guardar = useGuardarClaseDeSuelo();

  const [descripcion, setDescripcion] = useState(clase.descripcion ?? "");
  const [orden, setOrden] = useState(String(clase.orden));

  // Cuando el servidor devuelve lo guardado, el borrador vuelve a ser lo que
  // hay. Sin esto el botón quedaría habilitado para siempre después de guardar:
  // el borrador seguiría difiriendo de un original que ya cambió.
  useEffect(() => {
    setDescripcion(clase.descripcion ?? "");
    setOrden(String(clase.orden));
  }, [clase]);

  const cambiado =
    descripcion !== (clase.descripcion ?? "") || Number(orden) !== clase.orden;

  function enviar() {
    guardar.mutate(
      {
        id: clase.id,
        cambios: {
          // Vaciar la glosa la BORRA: `""` viaja como `null`, que es lo que la
          // columna guarda cuando no hay texto.
          descripcion: descripcion.trim() === "" ? null : descripcion.trim(),
          orden: Number(orden),
        },
      },
      {
        onError: (error) => {
          toast.error(
            getApiErrorMessage(error, "No pudimos guardar la clase. Inténtalo de nuevo."),
          );
        },
      },
    );
  }

  return (
    <TableRow>
      <TableCell className="font-mono text-sm font-bold">{clase.codigo}</TableCell>

      <TableCell>
        <Input
          aria-label={`Qué es la clase ${clase.codigo}`}
          className="h-9"
          placeholder="Sin glosa"
          value={descripcion}
          onChange={(evento) => setDescripcion(evento.target.value)}
        />
      </TableCell>

      <TableCell>
        <Input
          type="number"
          min={0}
          aria-label={`Orden de la clase ${clase.codigo}`}
          className="h-9 w-16 text-right font-mono tabular-nums"
          value={orden}
          onChange={(evento) => setOrden(evento.target.value)}
        />
      </TableCell>

      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            // Deshabilitado sin cambios: un botón siempre activo manda una
            // escritura que no cambia nada y deja sin saber si se guardó.
            disabled={!cambiado || guardar.isPending}
            onClick={enviar}
          >
            {guardar.isPending && <Spinner className="text-primary-foreground" />}
            Guardar
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={onDarDeBaja}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Dar de baja
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
